import { useCallback, useState } from 'react';
import { Box, Button, Chip, MenuItem, Stack, TextField, Typography } from '@mui/material';
import type {
  BillingAccessPermission,
  BillingAccessView,
  BillingPersonMatchView,
  SupportedLocale,
} from '@stm/contracts';
import { translate } from '@stm/i18n';
import { apiRequest } from './api';
import { formatCalendarDate } from './athletes';
import { accessPermissionKeys, accessPermissions } from './billing';
import { errorKeyOf, type ErrorKey } from './BillingForms';
import { RemoteSelect, type RemoteOption } from './RemoteSelect';

export interface BillingAccessSectionProps {
  locale: SupportedLocale;
  /** `/api/v1/organizations/{id}/billing`. */
  base: string;
  accountId: string;
  active: boolean;
  access: readonly BillingAccessView[];
  csrfToken: string;
  /** Reloads the account after the server accepted a change. */
  onChanged: () => Promise<void>;
  onError: (key: ErrorKey | null) => void;
}

const todayInUtc = (): string => new Date().toISOString().slice(0, 10);

/**
 * Who may see this account: approvals are per person and per account, independent of any guardian
 * link, so approving someone for one account opens no other and revoking takes effect at once.
 */
export function BillingAccessSection({
  locale,
  base,
  accountId,
  active,
  access,
  csrfToken,
  onChanged,
  onError,
}: BillingAccessSectionProps): React.JSX.Element {
  const [personId, setPersonId] = useState('');
  const [permission, setPermission] = useState<BillingAccessPermission>('view');
  const [validFrom, setValidFrom] = useState(todayInUtc);
  const [validUntil, setValidUntil] = useState('');
  const [searchKey, setSearchKey] = useState(0);

  const findPeople = useCallback(
    async (text: string): Promise<RemoteOption[]> => {
      if (text.length < 2) return [];
      const matches = await apiRequest<BillingPersonMatchView[]>(
        `${base}/people?search=${encodeURIComponent(text)}`,
      );
      return matches.map((match) => ({
        id: match.id,
        label:
          `${match.givenName} ${match.familyName}` +
          (match.birthYear === null ? '' : ` (${String(match.birthYear)})`),
      }));
    },
    [base],
  );

  async function grant(): Promise<void> {
    onError(null);
    try {
      await apiRequest(`${base}/accounts/${accountId}/access`, {
        method: 'POST',
        body: {
          personId,
          permission,
          validFrom,
          validUntil: validUntil === '' ? null : validUntil,
        },
        csrfToken,
      });
      setPersonId('');
      setValidUntil('');
      setSearchKey((current) => current + 1);
      await onChanged();
    } catch (error) {
      onError(errorKeyOf(error));
    }
  }

  async function revoke(entry: BillingAccessView): Promise<void> {
    onError(null);
    try {
      await apiRequest(`${base}/accounts/${accountId}/access/${entry.id}/revoke`, {
        method: 'POST',
        body: {},
        csrfToken,
        ifMatchVersion: entry.version,
      });
      await onChanged();
    } catch (error) {
      onError(errorKeyOf(error));
    }
  }

  return (
    <Box component="section" aria-labelledby="access-title" sx={{ mb: 3 }}>
      <Typography variant="h6" component="h3" id="access-title" gutterBottom>
        {translate(locale, 'billing.access.title')}
      </Typography>
      {access.length === 0 && <Typography>{translate(locale, 'billing.access.empty')}</Typography>}
      <Stack component="ul" spacing={1} sx={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {access.map((entry) => (
          <Box
            component="li"
            key={entry.id}
            sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}
          >
            <Typography component="span">
              {entry.personName} · {translate(locale, accessPermissionKeys[entry.permission])} ·{' '}
              {translate(locale, 'billing.access.from')}{' '}
              {formatCalendarDate(entry.validFrom, locale)}
              {entry.validUntil !== null &&
                ` ${translate(locale, 'billing.assignments.until')} ${formatCalendarDate(entry.validUntil, locale)}`}
            </Typography>
            {entry.revoked ? (
              <Chip size="small" label={translate(locale, 'billing.access.revoked')} />
            ) : (
              <Button
                size="small"
                color="error"
                onClick={() => {
                  void revoke(entry);
                }}
              >
                {translate(locale, 'billing.access.revoke')}
              </Button>
            )}
          </Box>
        ))}
      </Stack>
      {active && (
        <Stack
          component="form"
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ mt: 2, flexWrap: 'wrap' }}
          onSubmit={(event) => {
            event.preventDefault();
            if (personId !== '') void grant();
          }}
        >
          <RemoteSelect
            key={`access-${String(searchKey)}`}
            label={translate(locale, 'billing.access.personSearch')}
            noMatchesText={translate(locale, 'billing.access.noMatches')}
            load={findPeople}
            onSelect={setPersonId}
          />
          <TextField
            select
            size="small"
            label={translate(locale, 'billing.access.permission')}
            value={permission}
            onChange={(event) => {
              setPermission(event.target.value as BillingAccessPermission);
            }}
            sx={{ minWidth: 200 }}
          >
            {accessPermissions.map((option) => (
              <MenuItem key={option} value={option}>
                {translate(locale, accessPermissionKeys[option])}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            required
            size="small"
            type="date"
            label={translate(locale, 'billing.access.validFrom')}
            value={validFrom}
            onChange={(event) => {
              setValidFrom(event.target.value);
            }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            size="small"
            type="date"
            label={translate(locale, 'billing.access.validUntil')}
            value={validUntil}
            onChange={(event) => {
              setValidUntil(event.target.value);
            }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <Button type="submit" variant="outlined" disabled={personId === '' || validFrom === ''}>
            {translate(locale, 'billing.access.grant')}
          </Button>
        </Stack>
      )}
    </Box>
  );
}
